/**
 * The sequence document: one versioned, non-destructive model used for both
 * browser preview and server-side export. All positions are integer ticks
 * snapped to the sequence's frame grid; sources reference exact asset
 * versions so a sequence revision always means the same material.
 *
 * Tracks:
 *  - video: the main storyline (V1). Clips are contiguous in order; each
 *    clip's own audio (A1) follows it. A clip may start with a transition
 *    (dissolve, dip to black, wipe, slide) from the clip before it: the two
 *    overlap for the transition's length, so the storyline gets shorter.
 *  - layers: video tracks above V1 (V2, V3…), freely positioned, composited
 *    in order with each clip's opacity, size and position. Sources with an
 *    alpha channel keep their transparency.
 *  - audio: one additional audio track (A2), freely positioned. 1.7.3: audioTracks holds A3–A8 (up to 6 more), each
 *    like A2; a storyline clip whose sound was unlinked (soundDetached) is silent on A1, its sound is an audio clip.
 *  - graphics: HTML template instances (renderAt or OGraf, 0.93.0), freely positioned. 1.7.3: on up to three
 *    graphics tracks (GFX 1–3, `track`), drawn GFX 1 first and GFX 3 on top (graphicOrder).
 *  - 1.7.3 link groups: clips and graphics sharing a linkId select, move and delete together.
 *  - 1.7.4 sound levels: volume keys on any clip with sound, crossfades at audio and A1 cuts, a gain per sound track
 *    (audio-levels.ts); the sequence In and Out (marks, three-point.ts).
 *  - subtitles: structured cues in sequence time.
 *  - multicam: groups of synced angles; storyline clips cut from a group can
 *    switch to another angle at the same moment.
 *
 * Fields added after the first release are optional with defaults, so older
 * documents keep parsing unchanged.
 */
import { z } from 'zod';
import { CueSchema, TrackStyleSchema } from './subtitles.ts';
import { ColorCorrectionSchema } from './color.ts';
import { TICKS_PER_SECOND, ticksAfterStart, ticksPerFrame, type Rational } from './time.ts';
import { FIT_MODES, KEY_EASES, MAX_KEYS, VIDEO_FIT_MODES, ZOOM_MAX, ZOOM_MIN, sameAspect } from './framing.ts';

export const SEQUENCE_SCHEMA_VERSION = 1;

const Ticks = z.number().int().nonnegative();
/** A link group's id (1.7.3, newId('lk')). */
const LinkIdSchema = z.string().min(1).max(64);

/* ---------------------------------------------------------------- 1.7.4: sound levels (docs/dev/editor-1.7.4-build.md) */

/** The level range of a clip's gain, a volume key and a track's gain (dB), as gainDb has always had. */
export const MIN_DB = -60;
export const MAX_DB = 12;
/** At most this many volume keys on one clip. */
export const MAX_VOLUME_KEYS = 64;
const DbSchema = z.number().min(MIN_DB).max(MAX_DB);

/**
 * A volume key (1.7.4): at `atTicks` in SOURCE time (the clip's sourceInTicks time base, like framing keys, so keys
 * stay on the sound when the clip is trimmed, moved or split), a level of `db`. The keys are an offset on top of the
 * clip's gainDb: the level at source time t is gainDb + the keys' curve at t (audio-levels.ts volumeAt), the curve
 * linear in dB between keys and held flat before the first key and after the last. Strictly in time order.
 */
export const VolumeKeySchema = z.object({ atTicks: z.number().int().nonnegative(), db: DbSchema });
export type VolumeKey = z.infer<typeof VolumeKeySchema>;
const VolumeKeysSchema = z.array(VolumeKeySchema).min(1).max(MAX_VOLUME_KEYS).superRefine((keys, c) => {
  keys.forEach((k, i) => { if (i > 0 && k.atTicks <= keys[i - 1]!.atTicks) c.addIssue({ code: 'custom', path: [i, 'atTicks'], message: 'Keys must be in time order' }); });
});

/**
 * An audio crossfade into a clip (1.7.4), on the INCOMING clip of a cut: an audio clip whose start touches the end of
 * the clip before it on the same audio track, or a V1 clip at a straight cut (no gap, no transition; both clips' sound
 * on A1). At render time the incoming clip's sound starts `crossfadeInTicks` earlier, taken from its head handle
 * (source sourceInTicks − crossfadeInTicks … sourceInTicks), and fades in with an equal-power curve while the
 * outgoing clip's last `crossfadeInTicks` fade out (afade curve=qsin); the record positions, the picture and the
 * sequence length stay as they are. What applies is clamped by audio-levels.ts crossfadeLimits (the handle, the
 * outgoing clip's length, MAX_CROSSFADE_SECONDS); a cut that no longer qualifies applies none (the field is dormant).
 */
const CrossfadeSchema = z.number().int().positive();

/**
 * Where a crossfade sits against its cut (1.8.0), on the incoming clip beside crossfadeInTicks: 'center' blends over
 * [cut − X/2, cut + X/2] (the incoming sound X/2 early from its head handle, the outgoing one X/2 past its Out from its
 * tail handle), 'start' over [cut, cut + X] (the outgoing sound X past its Out). Left out: it ends at the cut, as in
 * 1.7.4, so older documents re-save byte for byte and render as before. audio-levels.ts crossfadeLimits clamps it.
 */
export const CROSSFADE_ALIGNS = ['center', 'start'] as const;
export type CrossfadeAlign = (typeof CROSSFADE_ALIGNS)[number] | 'end';
const CrossfadeAlignSchema = z.enum(CROSSFADE_ALIGNS);

const BaseClipSchema = z.object({
  id: z.string().min(1).max(64),
  assetId: z.string().uuid(),
  versionId: z.string().uuid(),
  label: z.string().max(200),
  sourceInTicks: Ticks,
  sourceOutTicks: Ticks,
  /** Linked source audio (A1 for the storyline). */
  gainDb: z.number().min(-60).max(12).default(0),
  muted: z.boolean().default(false),
  fadeInTicks: Ticks.default(0),
  fadeOutTicks: Ticks.default(0),
  /**
   * Picture fades (0.96.0): V1 fades from and to black, a layer clip from and to transparent. Optional and left
   * out when 0 (not defaulted), so documents without them re-save byte for byte. fadeInTicks/fadeOutTicks above
   * stay the sound fades; the timeline's fade handle sets both, the inspector can set them apart.
   */
  videoFadeInTicks: Ticks.optional(),
  videoFadeOutTicks: Ticks.optional(),
  /**
   * 1.7.3: the link group (docs/dev/editor-1.7.3-build.md): clips and graphics with the same linkId are selected,
   * moved and deleted together. Optional and left out when not linked, so documents re-save byte for byte.
   */
  linkId: LinkIdSchema.optional(),
  /** 1.7.4: volume keys (VolumeKeySchema), an offset on top of gainDb. Left out when none. */
  volumeKeys: VolumeKeysSchema.optional(),
});

/**
 * Framing (1.0.0-rc.11, framing.ts) of a picture clip (V1 and layers): Fit (left out, as before) or Fill, and for Fill
 * where the crop sits (−1…1 from the centre on each axis; left out when 0). Optional, so documents without them stay
 * valid and re-save byte for byte.
 */
/* ---------------------------------------------------------------- 1.7.0: formats, keyframed framing, layer motion */

/** At most this many formats besides the primary (the sequence's own width and height). */
export const MAX_FORMATS = 4;
/** The primary format's id wherever a format is named ('primary' or a SequenceFormat id). */
export const PRIMARY_FORMAT = 'primary' as const;
export type FormatRef = typeof PRIMARY_FORMAT | string;

const FormatIdSchema = z.string().min(1).max(64);

/** Keys sorted by time: strictly (framing keys), or not decreasing with no parameter set twice at one time (motion). */
function keysInOrder(keys: Array<{ atTicks: number } & Record<string, unknown>>, c: z.RefinementCtx, strict: boolean) {
  keys.forEach((k, i) => {
    const prev = keys[i - 1];
    if (!prev) return;
    if (k.atTicks < prev.atTicks || (strict && k.atTicks === prev.atTicks)) c.addIssue({ code: 'custom', path: [i, 'atTicks'], message: 'Keys must be in time order' });
    if (!strict) {
      for (let j = i - 1; j >= 0 && keys[j]!.atTicks === k.atTicks; j--) {
        for (const p of ['x', 'y', 'scale', 'opacity']) if (k[p] !== undefined && keys[j]![p] !== undefined) c.addIssue({ code: 'custom', path: [i, p], message: 'Two keys set the same value at the same time' });
      }
    }
  });
}

/**
 * A framing key (1.7.0): at `atTicks` in SOURCE time (the clip's sourceInTicks time base, so keys stay on the footage
 * when the clip is trimmed, moved or split), the Fill crop's pan (x, y: −1…1, the reframeX/reframeY space) and zoom
 * (1–8). The ease is that of the segment leaving the key. Keys only take effect with Fill.
 */
export const FramingKeySchema = z.object({
  atTicks: Ticks,
  x: z.number().min(-1).max(1),
  y: z.number().min(-1).max(1),
  zoom: z.number().min(ZOOM_MIN).max(ZOOM_MAX),
  ease: z.enum(KEY_EASES),
});
export type FramingKey = z.infer<typeof FramingKeySchema>;
const FramingKeysSchema = z.array(FramingKeySchema).max(MAX_KEYS).superRefine((keys, c) => keysInOrder(keys, c, true));

/**
 * A layer motion key (1.7.0), in source time like FramingKey: the layer's position (x, y), size (scale) and opacity,
 * each optional (a key sets only some of them; each parameter's curve runs through the keys that set it).
 */
export const MotionKeySchema = z.object({
  atTicks: Ticks,
  x: z.number().min(-1).max(1).optional(),
  y: z.number().min(-1).max(1).optional(),
  scale: z.number().min(0.05).max(4).optional(),
  opacity: z.number().min(0).max(1).optional(),
  ease: z.enum(KEY_EASES),
});
export type MotionKey = z.infer<typeof MotionKeySchema>;
export const LayerMotionSchema = z.object({ keys: z.array(MotionKeySchema).max(MAX_KEYS).superRefine((keys, c) => keysInOrder(keys, c, false)) });
export type LayerMotion = z.infer<typeof LayerMotionSchema>;

/** A layer's static placement in one format (1.7.0); a field left out is the primary's. */
export const LayerPlaceSchema = z.object({
  scale: z.number().min(0.05).max(4).optional(),
  x: z.number().min(-1).max(1).optional(),
  y: z.number().min(-1).max(1).optional(),
});
export type LayerPlace = z.infer<typeof LayerPlaceSchema>;

/** Where an item is hidden: 'primary' and format ids (1.7.0). Left out when shown everywhere. */
export const HideInSchema = z.array(FormatIdSchema).min(1).max(MAX_FORMATS + 1);

/**
 * Framing (1.0.0-rc.11, framing.ts) of a picture clip (V1 and layers): Fit (left out, as before) or Fill, and for Fill
 * where the crop sits (−1…1 from the centre on each axis; left out when 0). Optional, so documents without them stay
 * valid and re-save byte for byte. 1.7.0 adds the zoom (left out when 1) and keys (left out when none).
 */
const FramingFields = {
  fit: z.enum(FIT_MODES).optional(),
  reframeX: z.number().min(-1).max(1).optional(),
  reframeY: z.number().min(-1).max(1).optional(),
  zoom: z.number().min(ZOOM_MIN).max(ZOOM_MAX).optional(),
  keys: FramingKeysSchema.optional(),
};
/** A storyline clip's framing: as a layer's, and Blur (1.7.0). */
const VideoFramingFields = { ...FramingFields, fit: z.enum(VIDEO_FIT_MODES).optional() };

/**
 * A storyline clip's framing in another format (1.7.0, formatFraming[formatId]). `fit` present: the format has its own
 * framing (fields left out are their defaults: 0, zoom 1, no keys); `fit` left out: the same framing as the primary.
 */
export const VideoFormatFramingSchema = z.object(VideoFramingFields);
/**
 * A layer clip's framing, placement and motion in another format (1.7.0). Framing as VideoFormatFramingSchema (no
 * Blur); `place`: its own static scale, x and y (each left out: the primary's); `motion`: its own position and size
 * keys (x, y, scale; opacity keys are the primary's, shared by every format). Each left out: the same as the primary.
 */
export const LayerFormatFramingSchema = z.object({ ...FramingFields, place: LayerPlaceSchema.optional(), motion: LayerMotionSchema.optional() });
/** One format's entry of formatFraming (the union of the storyline and layer shapes). */
export type FormatFraming = Omit<z.infer<typeof LayerFormatFramingSchema>, 'fit'> & { fit?: (typeof VIDEO_FIT_MODES)[number] };

export const TRANSITION_KINDS = ['dissolve', 'dip_to_black', 'wipe', 'slide'] as const;
export type TransitionKind = (typeof TRANSITION_KINDS)[number];
export const TRANSITION_LABELS: Record<TransitionKind, string> = { dissolve: 'Cross dissolve', dip_to_black: 'Dip to black', wipe: 'Wipe', slide: 'Slide' };
/**
 * A transition (into a storyline clip from the one before it). 1.8.0: `headTicks` and `tailTicks` remember the media
 * ops.setTransition borrowed for it: the incoming clip's In moved `headTicks` earlier (its head handle) and the outgoing
 * clip's Out `tailTicks` later (its tail handle), so the cut and the sequence length stay; only durationTicks − head −
 * tail still overlaps. Removing or shortening the transition gives them back. Left out when nothing was borrowed (and on
 * every transition made before 1.8.0, which keep their overlap exactly), so older documents re-save byte for byte.
 */
export const TransitionSchema = z.object({ kind: z.enum(TRANSITION_KINDS), durationTicks: Ticks, headTicks: z.number().int().positive().optional(), tailTicks: z.number().int().positive().optional() });
export type Transition = z.infer<typeof TransitionSchema>;

export const VideoClipSchema = BaseClipSchema.extend({
  /** Transition from the previous storyline clip into this one (ignored on the first clip). */
  transitionIn: TransitionSchema.nullable().default(null),
  /** Set when the clip was cut from a multicam group: which group and angle. */
  multicam: z.object({ groupId: z.string().min(1).max(64), angle: z.number().int().min(0).max(15) }).nullable().default(null),
  /** Colour correction (0.96.0, color.ts); left out when neutral. */
  color: ColorCorrectionSchema.optional(),
  /**
   * Black and silence before this clip on the storyline (1.0.0-rc.7): a gap, as an NLE timeline checked back in can
   * have. Optional and left out when 0, so documents without gaps re-save byte for byte. A clip after a gap has no
   * transition into it (there is nothing to dissolve from).
   */
  gapBeforeTicks: Ticks.optional(),
  ...VideoFramingFields,
  /** Framing per other format (1.7.0), by format id; an entry left out (or the whole map): the same as the primary. */
  formatFraming: z.record(FormatIdSchema, VideoFormatFramingSchema).optional(),
  /**
   * 1.7.3: the clip's own sound is not on A1 (ops.unlink put it on an audio track as an audio clip). The preview and
   * the export treat the clip as silent on A1; its gainDb, muted and sound fades stay as they were (ops.relink
   * takes the audio clip's back). Left out when the sound is attached.
   */
  soundDetached: z.literal(true).optional(),
  /** 1.7.4: the A1 crossfade from the clip before (CrossfadeSchema); ignored with soundDetached. Left out when none. */
  crossfadeInTicks: CrossfadeSchema.optional(),
  /** 1.8.0: where the crossfade sits against the cut (CrossfadeAlignSchema); left out: it ends at the cut. */
  crossfadeAlign: CrossfadeAlignSchema.optional(),
});
export type VideoClip = z.infer<typeof VideoClipSchema>;

export const AudioClipSchema = BaseClipSchema.extend({
  recordInTicks: Ticks,
  /** 1.7.4: the crossfade from the clip before it on its track (CrossfadeSchema). Left out when none. */
  crossfadeInTicks: CrossfadeSchema.optional(),
  /** 1.8.0: where the crossfade sits against the cut (CrossfadeAlignSchema); left out: it ends at the cut. */
  crossfadeAlign: CrossfadeAlignSchema.optional(),
});
export type AudioClip = z.infer<typeof AudioClipSchema>;

export const LayerClipSchema = BaseClipSchema.extend({
  recordInTicks: Ticks,
  /** 0 (invisible) to 1 (opaque); multiplies any alpha channel in the source. */
  opacity: z.number().min(0).max(1).default(1),
  /** Size relative to fitting (or, with fit 'fill', filling) the frame (1 = the whole frame, 0.5 = half size). */
  scale: z.number().min(0.05).max(4).default(1),
  /** Offset of the picture's centre from the frame's centre, as a fraction of the frame width and height. */
  x: z.number().min(-1).max(1).default(0),
  y: z.number().min(-1).max(1).default(0),
  /** Colour correction (0.96.0, color.ts); left out when neutral. */
  color: ColorCorrectionSchema.optional(),
  ...FramingFields,
  /** Motion keys (1.7.0): position, size and opacity over time; left out when none. */
  motion: LayerMotionSchema.optional(),
  /** Framing, placement and motion per other format (1.7.0); left out: the same as the primary. */
  formatFraming: z.record(FormatIdSchema, LayerFormatFramingSchema).optional(),
  /** The formats it is hidden in (1.7.0); left out when shown in every format. */
  hideIn: HideInSchema.optional(),
});
export type LayerClip = z.infer<typeof LayerClipSchema>;

/* ---------------------------------------------------------------- 1.7.3: audio and graphics tracks */

/** Audio tracks after A2 (A3–A8): at most this many, so A1, A2 and these make eight. */
export const MAX_AUDIO_TRACKS = 6;
/** Graphics tracks GFX 1–3. */
export const MAX_GRAPHIC_TRACKS = 3;
/** The sound track ids that are not an audioTracks entry: the storyline's own sound and A2 (doc.audio). */
export const A1_TRACK = 'a1' as const;
export const A2_TRACK = 'a2' as const;

/**
 * An audio track after A2 (1.7.3): A3 is audioTracks[0]. Its clips are placed freely and never overlap (freeStart),
 * like A2. `id` is newId('t') (never 'a1' or 'a2'); trackAudio takes it for mute and solo.
 */
export const AudioTrackSchema = z.object({
  id: z.string().min(1).max(64).refine((id) => id !== A1_TRACK && id !== A2_TRACK, 'a1 and a2 are reserved track ids'),
  label: z.string().max(60),
  clips: z.array(AudioClipSchema).max(2000),
});
export type AudioTrack = z.infer<typeof AudioTrackSchema>;

/**
 * A graphic's track (1.7.3), counted from 0: 0 is GFX 1 (stored as `track` left out), 1 is GFX 2, 2 is GFX 3. On the
 * document `track` is only ever 1 or 2.
 */
export type GraphicTrack = 0 | 1 | 2;

export const VideoLayerSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().max(60),
  hidden: z.boolean().default(false),
  clips: z.array(LayerClipSchema).max(1000),
});

/* ---------------------------------------------------------------- markers and track sound (0.96.0) */

export const MARKER_COLORS = ['red', 'orange', 'yellow', 'green', 'blue', 'purple'] as const;
export const MAX_MARKERS = 500;
export const MarkerSchema = z.object({
  id: z.string().min(1).max(64),
  atTicks: Ticks,
  label: z.string().max(200),
  color: z.enum(MARKER_COLORS).optional(),
});
export type Marker = z.infer<typeof MarkerSchema>;

/**
 * Mute and solo of a sound track: 'a1' (the storyline's sound), 'a2', a video layer's id (its clips' sound) or (1.7.3)
 * an audio track's id (A3–A8). 1.7.4: `gainDb`, the track's volume, added to every clip's level on it (preview and
 * export); left out when 0.
 */
export const TrackAudioSchema = z.object({ mute: z.boolean().optional(), solo: z.boolean().optional(), gainDb: DbSchema.optional() });
export type TrackAudio = z.infer<typeof TrackAudioSchema>;
export type VideoLayer = z.infer<typeof VideoLayerSchema>;
export const MAX_LAYERS = 8;

export const MulticamGroupSchema = z.object({
  id: z.string().min(1).max(64),
  label: z.string().max(200),
  /** Angles in order (camera 1, 2…); startTicks is where each angle begins on the group's shared timeline. */
  angles: z.array(z.object({ assetId: z.string().uuid(), versionId: z.string().uuid(), label: z.string().max(200), startTicks: Ticks, durationTicks: Ticks })).min(2).max(16),
  /** How the angles were lined up. */
  syncedBy: z.enum(['timecode', 'start', 'manual']),
});
export type MulticamGroup = z.infer<typeof MulticamGroupSchema>;

/**
 * A field value: renderAt templates take strings (at most 500 characters, checked on save by engine); OGraf
 * graphics (0.93.0) take strings, numbers and booleans, checked on save against the version's manifest schema.
 */
export const GraphicValueSchema = z.union([z.string().max(2000), z.number(), z.boolean()]);
const GraphicDataSchema = z.record(z.string(), GraphicValueSchema);

export const GraphicInstanceSchema = z.object({
  id: z.string().min(1).max(64),
  templateId: z.string().min(1).max(80),
  templateVersion: z.number().int().positive(),
  recordInTicks: Ticks,
  durationTicks: Ticks,
  data: GraphicDataSchema,
  /**
   * OGraf (0.93.0): when each next step starts, in ticks from the clip start, ascending and inside the clip.
   * Optional and left out when empty (not defaulted), so documents without it re-save byte for byte.
   */
  stepMarks: z.array(Ticks).max(100).optional(),
  /** OGraf (0.93.0): data changes over time; each `data` is partial, merged onto the clip data. Left out when empty. */
  updates: z.array(z.object({ atTicks: Ticks, data: GraphicDataSchema })).max(100).optional(),
  /** The formats it is hidden in (1.7.0); left out when shown in every format. */
  hideIn: HideInSchema.optional(),
  /** 1.7.3: the graphics track above GFX 1, counted from 0 (1 = GFX 2, 2 = GFX 3; GraphicTrack). Left out on GFX 1. */
  track: z.union([z.literal(1), z.literal(2)]).optional(),
  /** 1.7.3: the link group, as on clips. Left out when not linked. */
  linkId: LinkIdSchema.optional(),
}).superRefine((g, c) => {
  g.stepMarks?.forEach((t, i) => {
    if (t >= g.durationTicks) c.addIssue({ code: 'custom', path: ['stepMarks', i], message: 'A step mark must be inside the graphic' });
    if (i > 0 && t <= g.stepMarks![i - 1]!) c.addIssue({ code: 'custom', path: ['stepMarks', i], message: 'Step marks must be in ascending order' });
  });
});
export type GraphicInstance = z.infer<typeof GraphicInstanceSchema>;

/** The subtitle line's bottom margin, as a fraction of the frame height (1.7.0, sequence-format.ts captionLayout). */
export const SubtitleBottomSchema = z.number().min(0.02).max(0.5);

/**
 * A format's framing for its whole storyline (1.7.1, sequence-format.ts documentForFormat): Fit, Fill or Blur for every
 * V1 clip in the format, over each clip's own; left out: per clip (1.7.0).
 */
export const FormatFitSchema = z.enum(VIDEO_FIT_MODES);

/**
 * Another format of the sequence (1.7.0): a frame size of its own shape (no two formats, nor a format and the primary,
 * share an aspect: framing.ts sameAspect), its label (default: the reduced ratio, "9:16"), its subtitle height and
 * (1.7.1) its framing for the whole storyline.
 * The same frame-size limits as the sequence; even sizes are checked where sizes are set (formatProblem).
 */
export const SequenceFormatSchema = z.object({
  id: FormatIdSchema.refine((id) => id !== PRIMARY_FORMAT, 'A format cannot be called primary'),
  width: z.number().int().min(16).max(7680),
  height: z.number().int().min(16).max(4320),
  label: z.string().max(60).optional(),
  subtitleBottom: SubtitleBottomSchema.optional(),
  fit: FormatFitSchema.optional(),
});
export type SequenceFormat = z.infer<typeof SequenceFormatSchema>;

/** Problems with a document's formats (the schema's refinement): ids unique, every aspect distinct (the primary's too). */
export function formatListIssues(doc: { width: number; height: number; formats?: Array<{ id: string; width: number; height: number }> }): Array<{ index: number; message: string }> {
  const out: Array<{ index: number; message: string }> = [];
  const fs = doc.formats ?? [];
  fs.forEach((f, i) => {
    if (fs.findIndex((g) => g.id === f.id) !== i) out.push({ index: i, message: 'Two formats have the same id' });
    if (sameAspect(f, doc)) out.push({ index: i, message: 'A format has the same shape as the sequence' });
    else if (fs.findIndex((g) => sameAspect(g, f)) !== i) out.push({ index: i, message: 'Two formats have the same shape' });
  });
  return out;
}

/** The sequence In and Out (1.7.4): at least one of them; In before Out. */
export const SequenceMarksSchema = z.object({ inTicks: Ticks.optional(), outTicks: Ticks.optional() })
  .refine((m) => m.inTicks !== undefined || m.outTicks !== undefined, 'Set an In or an Out')
  .refine((m) => m.inTicks === undefined || m.outTicks === undefined || m.inTicks < m.outTicks, 'The In must be before the Out');
export type SequenceMarks = z.infer<typeof SequenceMarksSchema>;

export const SequenceDocumentSchema = z.object({
  schemaVersion: z.literal(SEQUENCE_SCHEMA_VERSION),
  frameRate: z.object({ num: z.number().int().positive(), den: z.number().int().positive() }),
  width: z.number().int().min(16).max(7680),
  height: z.number().int().min(16).max(4320),
  sampleRate: z.literal(48000),
  video: z.array(VideoClipSchema).max(2000),
  audio: z.array(AudioClipSchema).max(2000),
  graphics: z.array(GraphicInstanceSchema).max(500),
  /** 1.9.0: `style`, the track's default style (cues may set their own); left out when none. */
  subtitles: z.object({ language: z.string(), label: z.string(), cues: z.array(CueSchema), style: TrackStyleSchema.optional() }).nullable(),
  /** Video tracks above the storyline, bottom (V2) to top. */
  layers: z.array(VideoLayerSchema).max(MAX_LAYERS).default([]),
  multicam: z.array(MulticamGroupSchema).max(50).default([]),
  /** Markers on the sequence (0.96.0), by time. Optional and left out when empty. */
  markers: z.array(MarkerSchema).max(MAX_MARKERS).optional(),
  /** Mute and solo per sound track (0.96.0), applied in the preview and the export. Left out when nothing is set. */
  trackAudio: z.record(z.string().min(1).max(64), TrackAudioSchema).optional(),
  /** Other formats (1.7.0): width and height above are the primary. Left out when none. */
  formats: z.array(SequenceFormatSchema).min(1).max(MAX_FORMATS).optional(),
  /** The primary's subtitle height (1.7.0, a fraction of the frame height); left out: the default by orientation. */
  subtitleBottom: SubtitleBottomSchema.optional(),
  /** The primary's framing for the whole storyline (1.7.1); left out: per clip. */
  fit: FormatFitSchema.optional(),
  /** 1.7.3: audio tracks A3–A8 (A2 stays `audio`). Left out when there are none. */
  audioTracks: z.array(AudioTrackSchema).min(1).max(MAX_AUDIO_TRACKS).optional(),
  /**
   * 1.7.3: how many graphics tracks the editor shows when that is more than the graphics use (empty tracks the user
   * added); graphicTrackCount is the larger of the two. Left out for one track.
   */
  graphicTracks: z.union([z.literal(2), z.literal(3)]).optional(),
  /**
   * 1.7.4: the sequence In and Out (record ticks, on the frame grid; three-point.ts): three-point editing, and "In to
   * Out" for the export, the EDL and Send to edit. Either may be left out (In: the start; Out: the end); the whole
   * object is left out when neither is set. In is before Out.
   */
  marks: SequenceMarksSchema.optional(),
}).superRefine((doc, c) => {
  for (const issue of formatListIssues(doc)) c.addIssue({ code: 'custom', path: ['formats', issue.index], message: issue.message });
  // 1.7.3: audio track ids unique (they key trackAudio like layer ids, so not a layer's id either).
  (doc.audioTracks ?? []).forEach((t, i) => {
    if (doc.audioTracks!.findIndex((u) => u.id === t.id) !== i || (doc.layers ?? []).some((l) => l.id === t.id)) c.addIssue({ code: 'custom', path: ['audioTracks', i, 'id'], message: 'Two tracks have the same id' });
  });
});
export type SequenceDocument = z.infer<typeof SequenceDocumentSchema>;

export function emptySequence(frameRate: Rational = { num: 25, den: 1 }, width = 1920, height = 1080): SequenceDocument {
  return { schemaVersion: 1, frameRate, width, height, sampleRate: 48000, video: [], audio: [], graphics: [], subtitles: null, layers: [], multicam: [] };
}

/* ---------------------------------------------------------------- frame grid */

export function snap(ticks: number, rate: Rational): number {
  const tpf = ticksPerFrame(rate);
  return Math.round(Math.round(ticks / tpf) * tpf);
}

export function clipDuration(c: { sourceInTicks: number; sourceOutTicks: number }): number {
  return Math.max(0, c.sourceOutTicks - c.sourceInTicks);
}

export interface PlacedClip<T> {
  clip: T;
  index: number;
  recordIn: number;
  recordOut: number;
  /** The transition into this clip as it applies (clamped to the clips' lengths), or null for a cut. */
  transition: { kind: TransitionKind; ticks: number } | null;
  /** Black before this clip (gapBeforeTicks on the frame grid), 0 when it follows the clip before it directly. */
  gapBefore: number;
}

/** A storyline clip's gap before it, on the frame grid (1.0.0-rc.7). */
export function gapBefore(clip: { gapBeforeTicks?: number }, rate: Rational): number {
  return clip.gapBeforeTicks ? Math.max(0, snap(clip.gapBeforeTicks, rate)) : 0;
}

/**
 * The longest transition allowed between two storyline clips: half of the
 * shorter clip, on the frame grid, so a clip's incoming and outgoing
 * transitions never overlap each other.
 */
export function maxTransitionTicks(prev: { sourceInTicks: number; sourceOutTicks: number }, next: { sourceInTicks: number; sourceOutTicks: number }, rate: Rational): number {
  const tpf = ticksPerFrame(rate);
  return Math.max(0, Math.floor(Math.min(clipDuration(prev), clipDuration(next)) / 2 / tpf) * tpf);
}

export function layoutVideo(doc: SequenceDocument): PlacedClip<VideoClip>[] {
  let t = 0;
  return doc.video.map((clip, index) => {
    const prev = doc.video[index - 1];
    const gap = gapBefore(clip, doc.frameRate);
    const want = index > 0 && clip.transitionIn && !gap ? clip.transitionIn.durationTicks : 0;
    const ticks = prev && want > 0 ? Math.min(snap(want, doc.frameRate), maxTransitionTicks(prev, clip, doc.frameRate)) : 0;
    const recordIn = t + gap - ticks;
    t = recordIn + clipDuration(clip);
    return { clip, index, recordIn, recordOut: t, transition: ticks > 0 ? { kind: clip.transitionIn!.kind, ticks } : null, gapBefore: gap };
  });
}

export function sequenceDuration(doc: SequenceDocument): number {
  const v = layoutVideo(doc).at(-1)?.recordOut ?? 0;
  // 1.7.3: every audio track (A2 and A3–A8).
  const a = Math.max(0, ...allAudioTracks(doc).flatMap((t) => t.clips.map((c) => c.recordInTicks + clipDuration(c))));
  const g = Math.max(0, ...doc.graphics.map((x) => x.recordInTicks + x.durationTicks));
  const l = Math.max(0, ...(doc.layers ?? []).flatMap((layer) => layer.clips.map((c) => c.recordInTicks + clipDuration(c))));
  return Math.max(v, a, g, l);
}

/**
 * Which storyline clip is under a sequence position, and where in its source.
 * Inside a transition both clips play; the incoming clip is returned, and
 * `outgoing` describes the clip it is transitioning from.
 */
export function resolveAt(doc: SequenceDocument, ticks: number): {
  placed: PlacedClip<VideoClip>; sourceTicks: number;
  outgoing?: { placed: PlacedClip<VideoClip>; sourceTicks: number; progress: number };
} | null {
  const placed = layoutVideo(doc);
  for (let i = placed.length - 1; i >= 0; i--) {
    const p = placed[i]!;
    if (ticks >= p.recordIn && ticks < p.recordOut) {
      const hit = { placed: p, sourceTicks: p.clip.sourceInTicks + (ticks - p.recordIn) };
      const prev = placed[i - 1];
      if (p.transition && prev && ticks < p.recordIn + p.transition.ticks) {
        return { ...hit, outgoing: { placed: prev, sourceTicks: prev.clip.sourceInTicks + (ticks - prev.recordIn), progress: (ticks - p.recordIn) / p.transition.ticks } };
      }
      return hit;
    }
  }
  return null;
}

/** Layer clips under a position, bottom layer first, with where in their sources. */
export function layersAt(doc: SequenceDocument, ticks: number): Array<{ layer: VideoLayer; layerIndex: number; clip: LayerClip; sourceTicks: number }> {
  const out: Array<{ layer: VideoLayer; layerIndex: number; clip: LayerClip; sourceTicks: number }> = [];
  (doc.layers ?? []).forEach((layer, layerIndex) => {
    if (layer.hidden) return;
    for (const clip of layer.clips) {
      if (ticks >= clip.recordInTicks && ticks < clip.recordInTicks + clipDuration(clip)) out.push({ layer, layerIndex, clip, sourceTicks: clip.sourceInTicks + (ticks - clip.recordInTicks) });
    }
  });
  return out;
}

/**
 * Every clip in the document with the track it is on. 1.7.3: A3–A8 clips are 'audio' too, with `audioTrackId` (left
 * out for A2, as before). They come after the layers', so the order of the entries before them is unchanged.
 */
export function allClips(doc: SequenceDocument): Array<{ track: 'video' | 'audio' | 'layer'; clip: VideoClip | AudioClip | LayerClip; layerId?: string; audioTrackId?: string }> {
  return [
    ...doc.video.map((clip) => ({ track: 'video' as const, clip })),
    ...doc.audio.map((clip) => ({ track: 'audio' as const, clip })),
    ...(doc.layers ?? []).flatMap((l) => l.clips.map((clip) => ({ track: 'layer' as const, clip, layerId: l.id }))),
    ...(doc.audioTracks ?? []).flatMap((t) => t.clips.map((clip) => ({ track: 'audio' as const, clip, audioTrackId: t.id }))),
  ];
}

/* ---------------------------------------------------------------- 1.7.3: track helpers */

/** An audio track as the editor, the export and the NLE writers list them: A2 first (doc.audio), then A3–A8. */
export interface AudioTrackView {
  /** 'a2' or the audioTracks entry's id. */
  id: string;
  label: string;
  clips: AudioClip[];
  /** The track's number: 2 for A2, 3 for audioTracks[0] … 8. */
  number: number;
}

/** A2 (`audio`) and then every audioTracks entry, in order. */
export function allAudioTracks(doc: SequenceDocument): AudioTrackView[] {
  return [
    { id: A2_TRACK, label: 'A2', clips: doc.audio, number: 2 },
    ...(doc.audioTracks ?? []).map((t, i) => ({ id: t.id, label: t.label, clips: t.clips, number: i + 3 })),
  ];
}

/** The audio track an audio clip is on ('a2' or a track id), or null when no audio track holds it. */
export function audioTrackOf(doc: SequenceDocument, clipId: string): string | null {
  if (doc.audio.some((c) => c.id === clipId)) return A2_TRACK;
  return (doc.audioTracks ?? []).find((t) => t.clips.some((c) => c.id === clipId))?.id ?? null;
}

/** A graphic's track, counted from 0 (0 = GFX 1). */
export const graphicTrackOf = (g: Pick<GraphicInstance, 'track'>): GraphicTrack => g.track ?? 0;

/** How many graphics tracks there are (1–3): the most the graphics use, or more when the user added empty ones. */
export function graphicTrackCount(doc: SequenceDocument): 1 | 2 | 3 {
  const used = Math.max(0, ...doc.graphics.map(graphicTrackOf)) + 1;
  return Math.min(MAX_GRAPHIC_TRACKS, Math.max(used, doc.graphicTracks ?? 1)) as 1 | 2 | 3;
}

/**
 * The graphics in drawing order (1.7.3), bottom first: by track (GFX 1, 2, 3), then by their order in the array.
 * What the preview's z-index, the export's overlays, graphicLayers and the NLE stacking all use. For a document
 * without tracks it is doc.graphics itself (the same array).
 */
export function graphicOrder(doc: SequenceDocument): GraphicInstance[] {
  if (!doc.graphics.some((g) => g.track)) return doc.graphics;
  return doc.graphics.map((g, i) => ({ g, i })).sort((a, b) => graphicTrackOf(a.g) - graphicTrackOf(b.g) || a.i - b.i).map((x) => x.g);
}

/** The source versions a document uses: clips on every track and multicam angles. */
export function sourceVersionIds(doc: SequenceDocument): string[] {
  return [...new Set([...allClips(doc).map((c) => c.clip.versionId), ...(doc.multicam ?? []).flatMap((g) => g.angles.map((a) => a.versionId))])];
}

/**
 * The source versions on the timeline (rc.9 fixes): clips on V1, the layers and the audio tracks only (A2, 1.7.3 A3–A8). A clip cut from a multicam
 * group is its cut angle's version (the clip's own versionId); the group's other angles, and groups no clip uses, are
 * not. What a hand-off sends and links; sourceVersionIds keeps the angles for saving and the editor.
 */
export function usedVersionIds(doc: SequenceDocument): string[] {
  return [...new Set(allClips(doc).map((c) => c.clip.versionId))];
}

/* ---------------------------------------------------------------- fades and track sound (0.96.0) */

/** The sound track a clip is heard on: 'a1' for the storyline, 'a2', its layer's id, or (1.7.3) its audio track's id. */
export type SoundTrack = 'a1' | 'a2' | string;

/**
 * Whether a sound track is heard: when any track is soloed only soloed tracks are, otherwise every track that is
 * not muted. A layer that no longer exists does not count.
 */
export function trackAudible(doc: SequenceDocument, track: SoundTrack): boolean {
  const st = doc.trackAudio;
  if (!st) return true;
  const known = (k: string) => k === 'a1' || k === 'a2' || (doc.layers ?? []).some((l) => l.id === k) || (doc.audioTracks ?? []).some((t) => t.id === k);
  const soloed = Object.entries(st).some(([k, v]) => v.solo && known(k));
  if (soloed) return Boolean(st[track]?.solo);
  return !st[track]?.mute;
}

/**
 * The picture fades as they apply (ticks), on the frame grid. On V1 the transitions into and out of the clip come
 * first: the fade in starts where the incoming transition ends and the fade out ends where the outgoing one starts,
 * so a fade and a transition never overlap, and both fades fit in what is left of the clip. A layer clip's fades fit
 * in the clip.
 */
export function pictureFades(c: { sourceInTicks: number; sourceOutTicks: number; videoFadeInTicks?: number; videoFadeOutTicks?: number }, rate: Rational, transitions: { inTicks: number; outTicks: number } = { inTicks: 0, outTicks: 0 }): { inTicks: number; outTicks: number; startTicks: number; endTicks: number } {
  const dur = clipDuration(c);
  const start = Math.min(dur, transitions.inTicks);
  const end = Math.max(start, dur - transitions.outTicks);
  const room = end - start;
  const fin = Math.min(room, Math.max(0, snap(c.videoFadeInTicks ?? 0, rate)));
  const fout = Math.min(room - fin, Math.max(0, snap(c.videoFadeOutTicks ?? 0, rate)));
  return { inTicks: fin, outTicks: fout, startTicks: start, endTicks: end };
}

/** The picture fades of every storyline clip, with the transitions on both sides taken into account. */
export function storylinePictureFades(doc: SequenceDocument, placed: PlacedClip<VideoClip>[] = layoutVideo(doc)): Array<ReturnType<typeof pictureFades>> {
  return placed.map((p, i) => pictureFades(p.clip, doc.frameRate, { inTicks: p.transition?.ticks ?? 0, outTicks: placed[i + 1]?.transition?.ticks ?? 0 }));
}

/** How far a clip is faded up (0 black or transparent … 1) at `local` ticks from its start. */
export function fadeFactor(f: { inTicks: number; outTicks: number; startTicks: number; endTicks: number }, local: number): number {
  if (f.inTicks > 0 && local < f.startTicks + f.inTicks) return Math.max(0, Math.min(1, (local - f.startTicks) / f.inTicks));
  if (f.outTicks > 0 && local > f.endTicks - f.outTicks) return Math.max(0, Math.min(1, (f.endTicks - local) / f.outTicks));
  return 1;
}

/** The sound fade factor (linear, as the export's afade) at `local` ticks into a clip of `dur` ticks. */
export function soundFadeFactor(c: { fadeInTicks: number; fadeOutTicks: number }, dur: number, local: number): number {
  let g = 1;
  if (c.fadeInTicks > 0 && local < c.fadeInTicks) g = Math.min(g, Math.max(0, local / c.fadeInTicks));
  if (c.fadeOutTicks > 0 && local > dur - c.fadeOutTicks) g = Math.min(g, Math.max(0, (dur - local) / c.fadeOutTicks));
  return g;
}

/* ---------------------------------------------------------------- multicam */

export interface MulticamSource {
  assetId: string;
  versionId: string;
  label: string;
  durationTicks: number;
  /** Start timecode embedded in the source, as ticks from midnight, if any. */
  startTimecodeTicks: number | null;
}

/**
 * Line angles up on a shared timeline: by embedded start timecode when every
 * angle has one (cameras jam-synced or fed the same timecode), otherwise by
 * their starts.
 */
export function syncAngles(sources: MulticamSource[], rate: Rational, by: 'timecode' | 'start' = 'timecode'): { syncedBy: 'timecode' | 'start'; angles: MulticamGroup['angles'] } {
  const useTc = by === 'timecode' && sources.every((s) => s.startTimecodeTicks != null);
  let starts = sources.map((s) => (useTc ? s.startTimecodeTicks! : 0));
  // 1.9.1, past midnight: angles more than half a day apart are on both sides of midnight (one started at 23:59,
  // another at 00:01, time-of-day timecode); the ones after midnight are on the next day.
  const day = 86_400 * TICKS_PER_SECOND;
  if (useTc && Math.max(...starts) - Math.min(...starts) > day / 2) starts = starts.map((t) => (t < day / 2 ? ticksAfterStart(t, day / 2, day) : t));
  const first = Math.min(...starts);
  return {
    syncedBy: useTc ? 'timecode' : 'start',
    angles: sources.map((s, i) => ({ assetId: s.assetId, versionId: s.versionId, label: s.label, startTicks: snap(starts[i]! - first, rate), durationTicks: s.durationTicks })),
  };
}

export function multicamDuration(g: MulticamGroup): number {
  return Math.max(0, ...g.angles.map((a) => a.startTicks + a.durationTicks));
}

/** Whether an angle has picture for the whole of [from, to) on the group's timeline. */
export function angleCovers(g: MulticamGroup, angle: number, from: number, to: number): boolean {
  const a = g.angles[angle];
  return Boolean(a) && from >= a!.startTicks && to <= a!.startTicks + a!.durationTicks;
}

/** A cut in a multicam edit: from `atTicks` on the group's timeline, show `angle`. */
export interface MulticamCut {
  atTicks: number;
  angle: number;
}
